import { useRef, useState } from "react";
import { useAtomValue, useSetAtom, useAtom } from "jotai";
import { useParams } from "wouter";
import { Center, Text3D } from "@react-three/drei";
import * as THREE from 'three';

import { 
  readyToStartAtom, 
  hostAtom, 
  clientAtom, 
  languageAtom,
  deviceAtom,
  minigameRuleAtom,
  spectatorsAtom,
  teamsAtom,
  resultsAtom
} from "../../GlobalState.jsx";
import { trackEvent } from '../../api';
import { getSocket } from "../../socket.js";
import MeshColors from "../MeshColors.jsx";
import translations from "../../dictionaries/translations.js";
import layout from "../../dictionaries/layout.js";
import PlayersParty from "./PlayersParty.jsx";
import AudioButtonSection from "./AudioButtonSection.jsx";

const socket = getSocket();

export default function FirstSectionNew({ position }) {
  const readyToStart = useAtomValue(readyToStartAtom)
  const host = useAtomValue(hostAtom)
  const client = useAtomValue(clientAtom)
  const teams = useAtomValue(teamsAtom)
  const spectators = useAtomValue(spectatorsAtom)
  const results = useAtomValue(resultsAtom)
  const minigameRule = useAtomValue(minigameRuleAtom)
  const isHost = client.socketId === host.socketId
  const [language, setLanguage] = useAtom(languageAtom)
  const device = useAtomValue(deviceAtom)
  const params = useParams();

  function StartGameButton({ position, scale }) {
    const [hover, setHover] = useState(false)
    const [pressed, setPressed] = useState(false)
    const lastStartGameAtRef = useRef(0)

    function handlePointerEnter(e) {
      e.stopPropagation()
      if (readyToStart) {
        document.body.style.cursor = 'pointer'
        setHover(true)
      }
    }
    function handlePointerLeave(e) {
      e.stopPropagation()
      if (readyToStart) {
        document.body.style.cursor = 'default'
        setHover(false)
      }
    }
    async function handlePointerDown (e) {
      e.stopPropagation()
      setHover(false)
      const now = Date.now()
      if (isHost && readyToStart && !pressed && now - lastStartGameAtRef.current >= 500) {
        lastStartGameAtRef.current = now
        setPressed(true)
        await trackEvent({
          surface: 'lobby',
          action: 'start_game',
          category: 'gameplay',
          metadata: {
            layout: 'first_section',
            players: teams.reduce((count, team) => count + team.players.length, 0),
            hasAi: teams.some((team) => team.players.some((player) => player.type === 'ai')),
            spectators: spectators.length,
            gameNumber: results.length + 1,
            minigameEnabled: minigameRule
          }
        })
        socket.emit('gameStartCountdown', { roomId: params.id.toUpperCase(), clientId: client._id })
      }
    }
    return <group name='start-game-button' position={position} scale={scale}>
      <mesh name='background-outer' scale={[4.5, 0.01, 0.9]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={ (readyToStart && isHost && !pressed) ? (hover ? 'green' : 'orange') : 'grey' }/>
      </mesh> 
      <mesh name='background-inner' scale={[4.45, 0.02, 0.85]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={MeshColors.spaceDark}/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[4.5, 0.02, 0.9]}
      onPointerEnter={e => handlePointerEnter(e)}
      onPointerLeave={e => handlePointerLeave(e)}
      onPointerDown={e => handlePointerDown(e)}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='orange' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.4}
          height={0.01}
        >
          {translations.lobby.startGame[language]}
          <meshStandardMaterial color={ (readyToStart && isHost && !pressed) ? (hover ? 'green' : 'orange') : 'grey' }/>
        </Text3D>
      </Center>
    </group>
  }
  function MenuButtons({ position, scale }) {
    return <group name='menu-buttons' position={position} scale={scale}>
      <AudioButtonSection position={layout[device].lobby.lobbySettingsSection.position}/>
    </group>
  }
  return <group position={position}>
    <group name='title' position={[-3.3,0,-5.3]}>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        rotation={[-Math.PI/2,0,0]}
        size={0.6}
        height={0.01}
      >
        {translations.lobby.yutNori[language]}
        <meshStandardMaterial color="orange"/>
      </Text3D>
      <Text3D
        font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
        position={[4.5,0,0]}
        rotation={[-Math.PI/2,0,0]}
        size={0.4}
        height={0.01}
      >
        {`${translations.lobby.roomId[language]}: ${params.id}${isHost ? ` (${translations.lobby.host[language]})` : ''}`}
        <meshStandardMaterial color="orange"/>
      </Text3D>
    </group>
    <PlayersParty position={[1, 0, -0.2]} scale={0.55}/>
    <MenuButtons position={[0.9, 4, 5.3]}/>
    <StartGameButton position={[0.9, 4, 6.7]} scale={1}/>
  </group>
}
